<template>
	<div
		class="bento-tile active-tasks"
		data-test="active-tasks-tile"
	>
		<div class="bento-tile__header">
			<span class="bento-tile__eyebrow">Active tasks</span>
			<v-btn
				v-if="!empty"
				variant="text"
				size="small"
				to="/tasks"
				class="bento-tile__action"
			>
				View all
				<ArrowRight16 />
			</v-btn>
		</div>
		<div
			v-if="empty"
			class="active-tasks__empty"
			data-test="active-tasks-empty"
		>
			<div class="active-tasks__empty-title sgnl-text-color-default sgnl-paragraph-s-medium">Nothing running</div>
			<div class="active-tasks__empty-body sgnl-text-color-secondary sgnl-caption-regular">
				{{
					tasksRun
						? "Idle — run a task to see live status here."
						: "Run your first task to see live status here."
				}}
			</div>
		</div>
		<template v-else>
			<div
				class="active-tasks__count bento-tile__stat sgnl-h5-medium"
				data-test="active-tasks-count"
			>
				{{ total }}
			</div>
			<div class="active-tasks__breakdown sgnl-text-color-secondary">
				<span
					v-if="activeCount > 0"
					class="active-tasks__breakdown-item sgnl-caption-regular"
				>
					<span class="active-tasks__dot active-tasks__dot--green" />
					{{ activeCount }} running
				</span>
				<span
					v-if="queuedCount > 0"
					class="active-tasks__breakdown-item sgnl-caption-regular"
				>
					<span class="active-tasks__dot active-tasks__dot--amber" />
					{{ queuedCount }} queued
				</span>
			</div>
			<div
				v-if="visibleRunning.length > 0"
				class="active-tasks__rows"
			>
				<div
					v-for="t in visibleRunning"
					:key="t.id"
					class="active-tasks__row sgnl-text-color-default sgnl-caption-regular"
				>
					<span class="active-tasks__row-id">{{ t.idShort }}</span>
					<span class="active-tasks__row-elapsed sgnl-text-color-brand">{{ t.elapsed }}</span>
					<router-link
						:to="`/tasks/${t.id}`"
						:aria-label="`Open task ${t.idShort}`"
						class="active-tasks__row-open"
					>
						<View16 />
					</router-link>
				</div>
				<div
					v-if="overflow > 0"
					class="active-tasks__overflow sgnl-text-color-secondary sgnl-footnote-regular"
				>
					+ {{ overflow }} more running
				</div>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import View16 from "@carbon/icons-vue/es/view/16";

import { mapState } from "pinia";
import { useTasksStore } from "@/stores/tasks";
import { isTaskActive, TaskStatusE, type Task } from "@/types/api/tasks";
import { convertToMilliseconds, formatElapsedMmSs, shortenID } from "@/js/utilities";

export default defineComponent({
	name: "ActiveTasksTile",
	components: { ArrowRight16, View16 },
	props: {
		tasksRun: { type: Number, default: 0 },
		// Shared 1s ticker owned by Home.vue; drives the elapsed mm:ss in `running` rows.
		now: { type: Number, required: true },
	},
	computed: {
		...mapState(useTasksStore, { activeTasks: "activeTasks" }),
		running(): { id: string; idShort: string; elapsed: string }[] {
			const list = (this.activeTasks as Task[] | undefined) || [];
			return list
				.filter((t) => t.Status === TaskStatusE.InProgress)
				.sort((a, b) => (a.StartedAt || 0) - (b.StartedAt || 0))
				.map((t) => {
					const startedMs = t.StartedAt ? convertToMilliseconds(t.StartedAt) : 0;
					return {
						id: t.TaskID,
						idShort: shortenID(t.TaskID),
						elapsed: formatElapsedMmSs(startedMs ? this.now - startedMs : 0),
					};
				});
		},
		activeCount(): number {
			const list = (this.activeTasks as Task[] | undefined) || [];
			return list.filter(
				(t) => isTaskActive(t.Status) && t.Status !== TaskStatusE.Pending && t.Status !== TaskStatusE.Accepted,
			).length;
		},
		queuedCount(): number {
			const list = (this.activeTasks as Task[] | undefined) || [];
			return list.filter((t) => t.Status === TaskStatusE.Pending || t.Status === TaskStatusE.Accepted).length;
		},
		total(): number {
			return this.activeCount + this.queuedCount;
		},
		empty(): boolean {
			return this.total === 0;
		},
		visibleRunning() {
			return this.running.slice(0, 3);
		},
		overflow(): number {
			return Math.max(0, this.running.length - 3);
		},
	},
});
</script>

<style lang="scss" scoped>
.active-tasks__empty {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--sgnl-space-8);
}

.active-tasks__count {
	color: var(--sgnl-default-primary-text);
}

.active-tasks__breakdown {
	display: flex;
	flex-wrap: wrap;
	gap: var(--sgnl-space-12);
}

.active-tasks__breakdown-item {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-6);
}

.active-tasks__dot {
	width: var(--sgnl-unit-8);
	height: var(--sgnl-unit-8);
	border-radius: 50%;
}
.active-tasks__dot--green {
	background: var(--sgnl-brand-background);
}
.active-tasks__dot--amber {
	background: var(--sgnl-alert-warning-icon);
}

.active-tasks__rows {
	padding-top: var(--sgnl-space-12);
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-6);
}

.active-tasks__row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: var(--sgnl-space-12);
}

.active-tasks__row-id {
	font-family: var(--sgnl-font-family-mono);
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.active-tasks__row-elapsed {
	font-family: var(--sgnl-font-family-mono);
	flex-shrink: 0;
	margin-left: auto;
}

.active-tasks__row-open {
	display: inline-flex;
	align-items: center;
	color: var(--sgnl-default-secondary-icon);
	flex-shrink: 0;
}

.active-tasks__row-open:hover {
	color: var(--sgnl-default-primary-icon);
}
</style>
